import React, {useCallback} from 'react';
import {useDrag, useDrop} from 'react-dnd';
import {Box, IconButton, Tooltip, Typography} from '@mui/material';
import {ArrowDownward, ArrowUpward, DragIndicator, LockOutlined} from '@mui/icons-material';
import {columnRowStyles} from './ColumnDesign';

export function reorderColumns(cards, id, atIndex) {
    const fromIndex = cards.findIndex(card => String(card.id) === String(id));
    if (fromIndex < 0 || atIndex < 0 || atIndex >= cards.length || fromIndex === atIndex || cards[fromIndex].frozen) return cards;
    // Frozen columns keep their positions when moving with either drag or keyboard controls.
    const start = Math.min(fromIndex, atIndex), end = Math.max(fromIndex, atIndex);
    if (cards.slice(start, end + 1).some(card => card.frozen)) return cards;
    const next = [...cards];
    const [card] = next.splice(fromIndex, 1);
    next.splice(atIndex, 0, card);
    return next;
}

function ColumnOrderRow({card, index, cards, moveCard, findCard, disabled}) {
    const lock = card.frozen;
    const [{isDragging}, drag] = useDrag(() => ({
        type: 'report-column', item: {id: card.id, originalIndex: index}, canDrag: !lock && !disabled,
        collect: monitor => ({isDragging: monitor.isDragging()}),
        end: (item, monitor) => {if (!monitor.didDrop()) moveCard(item.id, item.originalIndex);}
    }), [card.id, index, lock, disabled, moveCard]);
    const [, drop] = useDrop(() => ({
        accept: 'report-column', canDrop: () => !lock && !disabled,
        hover: item => {if (!lock && !disabled && item.id !== card.id) moveCard(item.id, findCard(card.id));}
    }), [card.id, lock, disabled, findCard, moveCard]);
    const canMoveUp = !disabled && !lock && index > 0 && !cards[index - 1].frozen;
    const canMoveDown = !disabled && !lock && index < cards.length - 1 && !cards[index + 1].frozen;

    return <Box ref={node => {drop(node);}} sx={{...columnRowStyles, opacity: isDragging ? 0.4 : 1}}>
        <Box ref={node => {drag(node);}} sx={{display: 'flex', color: 'text.secondary', flexShrink: 0,
            cursor: lock || disabled ? 'default' : 'grab'}}>
            {lock ? <LockOutlined sx={{fontSize: 17}}/> : <DragIndicator sx={{fontSize: 19}}/>}
        </Box>
        <Typography component="span" sx={{fontSize: 11, color: 'text.secondary', minWidth: 16}}>{index + 1}</Typography>
        <Box sx={{flex: 1, minWidth: 0}}>
            <Typography sx={{fontSize: 13, fontWeight: 600, overflowWrap: 'anywhere'}}>{card.title}</Typography>
            {card.formula && <Typography sx={{fontSize: 11, fontFamily: 'monospace', color: 'text.secondary', overflowWrap: 'anywhere', mt: 0.25}}>{card.formula}</Typography>}
        </Box>
        {!lock && <Box sx={{display: 'flex', flexShrink: 0}}>
            <Tooltip title="Move up"><span><IconButton size="small" aria-label={`Move ${card.title} up`} disabled={!canMoveUp}
                onClick={() => moveCard(card.id, index - 1)}><ArrowUpward sx={{fontSize: 16}}/></IconButton></span></Tooltip>
            <Tooltip title="Move down"><span><IconButton size="small" aria-label={`Move ${card.title} down`} disabled={!canMoveDown}
                onClick={() => moveCard(card.id, index + 1)}><ArrowDownward sx={{fontSize: 16}}/></IconButton></span></Tooltip>
        </Box>}
    </Box>;
}

export function DnDColumnsContainer({cards = [], setCards, disabled = false}) {
    const findCard = useCallback(id => cards.findIndex(card => String(card.id) === String(id)), [cards]);
    const moveCard = useCallback((id, atIndex) => {
        if (disabled) return;
        const next = reorderColumns(cards, id, atIndex);
        if (next !== cards) setCards(next);
    }, [cards, setCards, disabled]);
    const [, drop] = useDrop(() => ({accept: 'report-column', canDrop: () => !disabled}), [disabled]);
    return <Box ref={drop} sx={{display: 'grid', gap: 0.75, minWidth: 0}}>
        {cards.map((card, index) => <ColumnOrderRow key={card.id} card={card} index={index} cards={cards}
            moveCard={moveCard} findCard={findCard} disabled={disabled}/>)}
    </Box>;
}
